import type { EnrouteKitRow, MemberBrowseRow, MemberResultFacets, PcpBrowseRow, ResultsRow, ShippingRow } from '@lib/care-model';
import { dnsCharm } from '@lib/lookup-charms';
import { RESULT_STATUS_LABELS, RESULT_VALUE_LABELS, flagLabel, snomedLabel } from '@lib/result-codes';

/**
 * lookup-axes - the slicer rail's axis builders + row matchers for the browse-all Lookup tabs
 * (Portal buildMemberAxes / matchMemberOps / buildPcpAxes / matchPcpOps parity, trimmed to the
 * dimensions Member Care's rows carry).
 *
 * Contract (identical to the Portal rail): within an axis the selected values OR together; across
 * axes they AND. An axis with no selection passes every row; an axis whose option pool is empty
 * self-hides in the rail. The PCP axes apply PANEL-CONTAINS semantics - a PCP passes iff at least
 * one panel member would - over the char(31)-joined *_values sets the API aggregates.
 */

export interface LookupAxis {
  id: string;
  label: string;
  options: string[];
  /** Display formatter for chips; the option string stays the canonical filter value. */
  labelFor?: (value: string) => string;
  /** Section header; consecutive axes sharing a group render under one header. */
  group?: string;
  /** High-cardinality axes get a search box + tighter chip cap (ZIP / MSO / IPA / County). */
  searchable?: boolean;
}

/** axisId -> selected values */
export type LookupFilters = Record<string, string[]>;

export const EMPTY_LOOKUP_FILTERS: LookupFilters = {};

export function activeFilterCount(f: LookupFilters): number {
  return Object.values(f).reduce((n, arr) => n + (arr ? arr.length : 0), 0);
}

function axisSel(f: LookupFilters, id: string): string[] {
  return f[id] ?? [];
}

/** Row value `v` passes axis `id` iff the axis has no selection or v is selected. */
function axisPass(f: LookupFilters, id: string, v: string): boolean {
  const sel = axisSel(f, id);
  return sel.length === 0 || sel.includes(v);
}

/** Split a char(31) (ASCII Unit Separator) joined panel set (see PcpBrowseRow). -> []. */
export function splitMultiUS(s: string | null | undefined): string[] {
  return String(s ?? '').split('\u001f').map((x) => x.trim()).filter(Boolean);
}

/** Split a ','-joined multi-value column (panel result_values / result_flags; codes are comma-free). */
export function splitMulti(s: string | null | undefined): string[] {
  return String(s ?? '').split(',').map((x) => x.trim()).filter(Boolean);
}

/** Split one "comp_2024|comp_2025" pair on the FIRST '|' only. Returns [c24, c25]; missing side ''. */
export function splitCompPair(s: string): [string, string] {
  const i = s.indexOf('|');
  return i < 0 ? [s, ''] : [s.slice(0, i), s.slice(i + 1)];
}

/** Panel-contains: a PCP passes axis `id` iff at least one panel value maps to a selected option. */
function panelPass(
  f: LookupFilters,
  id: string,
  valuesCol: string | null | undefined,
  map?: (v: string) => string,
): boolean {
  const sel = axisSel(f, id);
  if (sel.length === 0) return true;
  return splitMultiUS(valuesCol).some((v) => sel.includes(map ? map(v) : v));
}

function uniq(arr: Array<string | null | undefined>): string[] {
  return [...new Set(arr.filter((v): v is string => !!v))].sort();
}

// ── Banding + label helpers (Portal parity) ─────────────────────────────────

/** Member age -> coarse band, ops-redline order. Non-numeric -> "Unknown". */
export const AGE_BANDS = ['<50', '50-64', '65-74', '75-84', '85+'] as const;
export function ageBand(age: string | null | undefined): string {
  const a = Number(age);
  if (age == null || age === '' || Number.isNaN(a)) return 'Unknown';
  if (a < 50) return '<50';
  if (a <= 64) return '50-64';
  if (a <= 74) return '65-74';
  if (a <= 84) return '75-84';
  return '85+';
}

/** comp_2024 / comp_2025 screening-status code -> label (Y/N/blank). */
export const COMP_LABELS = ['Compliant', 'Non-compliant', 'No record'] as const;
export function compLabel(v: string | null | undefined): string {
  const c = (v ?? '').trim().toUpperCase();
  return c === 'Y' ? 'Compliant' : c === 'N' ? 'Non-compliant' : 'No record';
}

/** Screening segment from the two comp flags (2025 takes precedence, Y/N only). */
export const SEGMENT_ORDER = ['Lapsed 2025', 'Persistent gap', 'Prior screener 2024', 'Prior gap 2024', 'No prior history'] as const;
export function memberSegment(c24: string | null | undefined, c25: string | null | undefined): string {
  const a = (c24 ?? '').trim().toUpperCase();
  const b = (c25 ?? '').trim().toUpperCase();
  if (b === 'Y') return 'Lapsed 2025';
  if (b === 'N') return 'Persistent gap';
  if (a === 'Y') return 'Prior screener 2024';
  if (a === 'N') return 'Prior gap 2024';
  return 'No prior history';
}

/** Display label for a cohort string (capitalized descriptor; filter values stay raw). */
export function cohortLabel(c: string | null | undefined): string {
  return String(c ?? '').replace(/ - ([a-z])/, (_m, ch: string) => ' - ' + ch.toUpperCase());
}

/** PCP panel size -> band, fixed order. */
export const PANEL_BANDS = ['1-10', '11-50', '51-200', '200+'] as const;
export function panelBand(panelSize: number | null | undefined): string {
  const n = Number(panelSize) || 0;
  if (n <= 10) return '1-10';
  if (n <= 50) return '11-50';
  if (n <= 200) return '51-200';
  return '200+';
}

// ── Member tab ───────────────────────────────────────────────────────────────

export function matchMemberOps(m: MemberBrowseRow, f: LookupFilters): boolean {
  if (!axisPass(f, 'county', m.county)) return false;
  if (!axisPass(f, 'state', m.state)) return false;
  if (!axisPass(f, 'zip', m.zip)) return false;
  if (!axisPass(f, 'market', m.market)) return false;
  if (!axisPass(f, 'gender', m.gender)) return false;
  if (!axisPass(f, 'ageBand', ageBand(m.age))) return false;
  if (!axisPass(f, 'language', m.language)) return false;
  if (!axisPass(f, 'cohort', m.cohort)) return false;
  if (!axisPass(f, 'mso', m.mso)) return false;
  if (!axisPass(f, 'ipa', m.ipa)) return false;
  if (!axisPass(f, 'segment', memberSegment(m.comp_2024, m.comp_2025))) return false;
  if (!axisPass(f, 'comp2025', compLabel(m.comp_2025))) return false;
  if (!axisPass(f, 'comp2024', compLabel(m.comp_2024))) return false;
  if (!axisPass(f, 'hasEmail', m.has_email ? 'Has email' : 'No email')) return false;
  if (!axisPass(f, 'hasPhone', m.has_phone ? 'Has phone' : 'No phone')) return false;
  if (!axisPass(f, 'doNotSolicit', dnsCharm(m) ? 'Do not solicit' : '')) return false;
  if (!axisPass(f, 'kitStage', m.kit_stage)) return false;
  if (!axisPass(f, 'deliveryStatus', m.delivery_status || 'No scan yet')) return false;
  // THE FOUR RESULT AXES ARE NOT MATCHED HERE (QA-113 option 4, William 2026-09-06). resultValue,
  // resultStatus, resulted and deliveredToLab are applied SERVER-side by /api/care/member-results,
  // which is the whole point: matching them here would need every member's outcome in the browser,
  // which is the disclosure being removed. The section passes the server's id set down instead, so
  // this function only ever sees the axes whose data is already on a browse row.
  //
  // A missing case here is therefore SAFE BY CONSTRUCTION rather than by care: the row has no
  // outcome field to consult, so a forgotten axis cannot silently pass every member.
  return true;
}

/** The Result axes, whose values live on the server. RESULT_AXIS_IDS is the one list; the section
 *  sends exactly these to the API and this file refuses to match them locally. */
export const RESULT_AXIS_IDS = ['resultValue', 'resultStatus', 'resulted', 'deliveredToLab'] as const;
export type ResultAxisId = (typeof RESULT_AXIS_IDS)[number];

/** Split a filter set into the axes this file matches and the axes the server matches. */
export function splitResultFilters(f: LookupFilters): { local: LookupFilters; result: LookupFilters } {
  const local: LookupFilters = {};
  const result: LookupFilters = {};
  for (const [id, vals] of Object.entries(f)) {
    if (!vals || vals.length === 0) continue;
    if ((RESULT_AXIS_IDS as readonly string[]).includes(id)) result[id] = vals;
    else local[id] = vals;
  }
  return { local, result };
}

/** Is any Result axis selected? The predicate that decides whether an outcome may travel at all. */
export function hasResultSelection(f: LookupFilters): boolean {
  return RESULT_AXIS_IDS.some((id) => (f[id] ?? []).length > 0);
}

/** The API's `selection` body, built from the rail's chips. The charm axes fold into `milestone`. */
export function toResultSelection(f: LookupFilters): {
  resultValue: string[];
  resultStatus: string[];
  milestone: Array<'resulted' | 'delivered_to_lab'>;
} {
  const milestone: Array<'resulted' | 'delivered_to_lab'> = [];
  if ((f.resulted ?? []).length) milestone.push('resulted');
  if ((f.deliveredToLab ?? []).length) milestone.push('delivered_to_lab');
  return {
    resultValue: [...(f.resultValue ?? [])],
    resultStatus: [...(f.resultStatus ?? [])],
    milestone,
  };
}

export function buildMemberAxes(rows: MemberBrowseRow[], facets?: MemberResultFacets): LookupAxis[] {
  if (rows.length === 0) return [];
  const ageBandsPresent = new Set(rows.map((m) => ageBand(m.age)));
  const axes: LookupAxis[] = [
    // ── Geography ──
    { id: 'county', label: 'County', group: 'Geography', options: uniq(rows.map((m) => m.county)), searchable: true },
    { id: 'state', label: 'State', group: 'Geography', options: uniq(rows.map((m) => m.state)) },
    { id: 'zip', label: 'ZIP', group: 'Geography', options: uniq(rows.map((m) => m.zip)), searchable: true },
    { id: 'market', label: 'Market', group: 'Geography', options: uniq(rows.map((m) => m.market)) },
    // ── Demographics ──
    { id: 'gender', label: 'Gender', group: 'Demographics', options: uniq(rows.map((m) => m.gender)) },
    { id: 'ageBand', label: 'Age band', group: 'Demographics', options: [...AGE_BANDS, 'Unknown'].filter((b) => ageBandsPresent.has(b)) },
    { id: 'language', label: 'Language', group: 'Demographics', options: uniq(rows.map((m) => m.language)), searchable: true },
    // ── Program ──
    { id: 'cohort', label: 'Cohort', group: 'Program', options: uniq(rows.map((m) => m.cohort)), labelFor: cohortLabel },
    { id: 'mso', label: 'MSO', group: 'Program', options: uniq(rows.map((m) => m.mso)), searchable: true },
    { id: 'ipa', label: 'IPA', group: 'Program', options: uniq(rows.map((m) => m.ipa)), searchable: true },
    // ── Screening history ──
    { id: 'segment', label: 'Screening segment', group: 'Screening history',
      options: SEGMENT_ORDER.filter((s) => rows.some((m) => memberSegment(m.comp_2024, m.comp_2025) === s)) },
    { id: 'comp2025', label: '2025 status', group: 'Screening history',
      options: COMP_LABELS.filter((l) => rows.some((m) => compLabel(m.comp_2025) === l)) },
    { id: 'comp2024', label: '2024 status', group: 'Screening history',
      options: COMP_LABELS.filter((l) => rows.some((m) => compLabel(m.comp_2024) === l)) },
    // ── Reachability ──
    { id: 'hasEmail', label: 'Has email', group: 'Reachability',
      options: uniq(rows.map((m) => (m.has_email ? 'Has email' : 'No email'))) },
    { id: 'hasPhone', label: 'Has phone', group: 'Reachability',
      options: uniq(rows.map((m) => (m.has_phone ? 'Has phone' : 'No phone'))) },
    // ── Status: the do-not-solicit charm only. The two MILESTONE charms moved to the Results
    //    group below, because they are derived from a member's result and their pools now come
    //    from the server's counts rather than from the rows (QA-113 option 4).
    { id: 'doNotSolicit', label: 'Do not solicit', group: 'Status',
      options: rows.some((m) => dnsCharm(m)) ? ['Do not solicit'] : [] },
    // ── Kit & outreach ──
    { id: 'kitStage', label: 'Kit stage', group: 'Kit & outreach', options: uniq(rows.map((m) => m.kit_stage)) },
    { id: 'deliveryStatus', label: 'Delivery status', group: 'Kit & outreach',
      options: uniq(rows.map((m) => m.delivery_status || 'No scan yet')) },
    // ── Results: pools + counts from the SERVER'S FACETS, never from the rows ──
    //
    // This is the half of option 4 that keeps Marny's filters alive. The old code asked
    // `rows.some((m) => m.result_label === lbl)`, which is only answerable if every row carries an
    // outcome. The counts answer the same question, "does this campaign have any Positive members,
    // and how many", without naming one of them. `countFor` puts the number on the chip, which is
    // strictly MORE than the operator had before: the rail used to show a chip with no number.
    //
    // Order still comes from the fixed label tables, so the rail reads Positive, Negative,
    // Indeterminate ... rather than in count order, and a label the tables do not carry (an unknown
    // SNOMED code falling back to itself) is appended after them rather than dropped.
    ...resultAxes(facets),
  ];
  return axes.filter((a) => a.options.length > 0);
}

/** Label ordering: the fixed table first, then anything the data carries that the table does not. */
function orderedFacetLabels(counts: Record<string, number>, table: readonly string[]): string[] {
  const known = table.filter((lbl) => (counts[lbl] ?? 0) > 0);
  const extra = Object.keys(counts).filter((lbl) => (counts[lbl] ?? 0) > 0 && !table.includes(lbl)).sort();
  return [...known, ...extra];
}

/** The three server-backed Result axes. With no facets (an older response, or a failed facet read)
 *  they self-hide, which is why `facets` is optional rather than required: a rail with no Result
 *  chips is a degraded rail, and a crash is not. */
function resultAxes(facets?: MemberResultFacets): LookupAxis[] {
  if (!facets || !facets.available) return [];
  const count = (rec: Record<string, number>, k: string): string => {
    const n = rec[k] ?? 0;
    return n > 0 ? `${k} (${n.toLocaleString()})` : k;
  };
  const milestoneOpts: string[] = [];
  if ((facets.milestone.resulted ?? 0) > 0) milestoneOpts.push('Resulted');
  if ((facets.milestone.delivered_to_lab ?? 0) > 0) milestoneOpts.push('Delivered to lab');
  return [
    { id: 'resultValue', label: 'Result value', group: 'Results',
      options: orderedFacetLabels(facets.resultValue, RESULT_VALUE_LABELS),
      labelFor: (v) => count(facets.resultValue, v) },
    { id: 'resultStatus', label: 'Result status', group: 'Results',
      options: orderedFacetLabels(facets.resultStatus, RESULT_STATUS_LABELS),
      labelFor: (v) => count(facets.resultStatus, v) },
    // The two milestone charms stay SEPARATE axes, keeping their existing ids so a saved filter set
    // and every existing test still address them by the same name.
    { id: 'resulted', label: 'Resulted', group: 'Results',
      options: milestoneOpts.includes('Resulted') ? ['Resulted'] : [],
      labelFor: () => `Resulted (${(facets.milestone.resulted ?? 0).toLocaleString()})` },
    { id: 'deliveredToLab', label: 'Delivered to lab', group: 'Results',
      options: milestoneOpts.includes('Delivered to lab') ? ['Delivered to lab'] : [],
      labelFor: () => `Delivered to lab (${(facets.milestone.delivered_to_lab ?? 0).toLocaleString()})` },
  ];
}

// ── PCP tab (panel-contains) ─────────────────────────────────────────────────

export function matchPcpOps(p: PcpBrowseRow, f: LookupFilters): boolean {
  // ── Geography ──
  if (!panelPass(f, 'county', p.county_values)) return false;
  if (!panelPass(f, 'state', p.state_values)) return false;
  if (!panelPass(f, 'zip', p.zip_values)) return false;
  if (!panelPass(f, 'market', p.market_values)) return false;
  // ── Demographics ──
  if (!panelPass(f, 'gender', p.gender_values)) return false;
  if (!panelPass(f, 'ageBand', p.age_values, ageBand)) return false;
  if (!panelPass(f, 'language', p.language_values)) return false;
  // ── Program ── (MSO/IPA filter on the panel SET; the column shows the dominant value)
  if (!panelPass(f, 'cohort', p.cohort_values)) return false;
  if (!panelPass(f, 'mso', p.mso_values)) return false;
  if (!panelPass(f, 'ipa', p.ipa_values)) return false;
  // ── Panel size (PCP-only) ──
  if (!axisPass(f, 'panelBand', panelBand(p.panel_size))) return false;
  // ── Screening history (from the panel's distinct comp pairs) ──
  const segSel = axisSel(f, 'segment');
  const c25Sel = axisSel(f, 'comp2025');
  const c24Sel = axisSel(f, 'comp2024');
  if (segSel.length || c25Sel.length || c24Sel.length) {
    const pairs = splitMultiUS(p.comp_pairs).map(splitCompPair);
    if (segSel.length && !pairs.some(([a, b]) => segSel.includes(memberSegment(a, b)))) return false;
    if (c25Sel.length && !pairs.some(([, b]) => c25Sel.includes(compLabel(b)))) return false;
    if (c24Sel.length && !pairs.some(([a]) => c24Sel.includes(compLabel(a)))) return false;
  }
  // ── Reachability (email_values / phone_values carry the Has/No labels) ──
  if (!panelPass(f, 'hasEmail', p.email_values)) return false;
  if (!panelPass(f, 'hasPhone', p.phone_values)) return false;
  // ── Status (panel-contains over the charms) ──
  if (!axisPass(f, 'doNotSolicit', p.any_dns ? 'Do not solicit' : '')) return false;
  if (!axisPass(f, 'deliveredToLab', splitMultiUS(p.kit_stage_values).includes('Sample returned') ? 'Delivered to lab' : '')) return false;
  if (!axisPass(f, 'resulted', p.resulted > 0 ? 'Resulted' : '')) return false;
  // ── Kit & outreach ──
  if (!panelPass(f, 'kitStage', p.kit_stage_values)) return false;
  if (!panelPass(f, 'deliveryStatus', p.delivery_values)) return false;
  // ── Results (','-joined coded tokens) ──
  const rvSel = axisSel(f, 'resultValue');
  if (rvSel.length && !splitMulti(p.result_values).some((c) => rvSel.includes(snomedLabel(c)))) return false;
  const rsSel = axisSel(f, 'resultStatus');
  if (rsSel.length && !splitMulti(p.result_flags).some((fl) => rsSel.includes(flagLabel(fl)))) return false;
  return true;
}

export function buildPcpAxes(rows: PcpBrowseRow[]): LookupAxis[] {
  if (rows.length === 0) return [];
  const poolUS = (col: keyof PcpBrowseRow): string[] => {
    const s = new Set<string>();
    for (const p of rows) for (const v of splitMultiUS(p[col] as string | null)) s.add(v);
    return [...s].sort();
  };
  const presentMapped = (col: keyof PcpBrowseRow, domain: readonly string[], map: (v: string) => string): string[] =>
    domain.filter((d) => rows.some((p) => splitMultiUS(p[col] as string | null).some((v) => map(v) === d)));
  const pairsOf = (p: PcpBrowseRow) => splitMultiUS(p.comp_pairs).map(splitCompPair);
  const segPresent = SEGMENT_ORDER.filter((s) => rows.some((p) => pairsOf(p).some(([a, b]) => memberSegment(a, b) === s)));
  const c25Present = COMP_LABELS.filter((l) => rows.some((p) => pairsOf(p).some(([, b]) => compLabel(b) === l)));
  const c24Present = COMP_LABELS.filter((l) => rows.some((p) => pairsOf(p).some(([a]) => compLabel(a) === l)));
  const panelBandsPresent = new Set(rows.map((p) => panelBand(p.panel_size)));
  const rvPresent = new Set<string>();
  for (const p of rows) for (const c of splitMulti(p.result_values)) rvPresent.add(snomedLabel(c));
  const rsPresent = new Set<string>();
  for (const p of rows) for (const fl of splitMulti(p.result_flags)) rsPresent.add(flagLabel(fl));

  const axes: LookupAxis[] = [
    // ── Geography ──
    { id: 'county', label: 'County', group: 'Geography', options: poolUS('county_values'), searchable: true },
    { id: 'state', label: 'State', group: 'Geography', options: poolUS('state_values') },
    { id: 'zip', label: 'ZIP', group: 'Geography', options: poolUS('zip_values'), searchable: true },
    { id: 'market', label: 'Market', group: 'Geography', options: poolUS('market_values') },
    // ── Demographics ──
    { id: 'gender', label: 'Gender', group: 'Demographics', options: poolUS('gender_values') },
    { id: 'ageBand', label: 'Age band', group: 'Demographics', options: presentMapped('age_values', [...AGE_BANDS, 'Unknown'], ageBand) },
    { id: 'language', label: 'Language', group: 'Demographics', options: poolUS('language_values'), searchable: true },
    // ── Program ──
    { id: 'cohort', label: 'Cohort', group: 'Program', options: poolUS('cohort_values'), labelFor: cohortLabel },
    { id: 'mso', label: 'MSO', group: 'Program', options: poolUS('mso_values'), searchable: true },
    { id: 'ipa', label: 'IPA', group: 'Program', options: poolUS('ipa_values'), searchable: true },
    // ── Panel (PCP-specific) ──
    { id: 'panelBand', label: 'Panel size', group: 'Panel', options: PANEL_BANDS.filter((b) => panelBandsPresent.has(b)) },
    // ── Screening history ──
    { id: 'segment', label: 'Screening segment', group: 'Screening history', options: segPresent },
    { id: 'comp2025', label: '2025 status', group: 'Screening history', options: c25Present },
    { id: 'comp2024', label: '2024 status', group: 'Screening history', options: c24Present },
    // ── Reachability ──
    { id: 'hasEmail', label: 'Has email', group: 'Reachability', options: poolUS('email_values') },
    { id: 'hasPhone', label: 'Has phone', group: 'Reachability', options: poolUS('phone_values') },
    // ── Status ──
    { id: 'doNotSolicit', label: 'Do not solicit', group: 'Status',
      options: rows.some((p) => p.any_dns > 0) ? ['Do not solicit'] : [] },
    { id: 'deliveredToLab', label: 'Delivered to lab', group: 'Status',
      options: rows.some((p) => splitMultiUS(p.kit_stage_values).includes('Sample returned')) ? ['Delivered to lab'] : [] },
    { id: 'resulted', label: 'Resulted', group: 'Status',
      options: rows.some((p) => p.resulted > 0) ? ['Resulted'] : [] },
    // ── Kit & outreach ──
    { id: 'kitStage', label: 'Kit stage', group: 'Kit & outreach', options: poolUS('kit_stage_values') },
    { id: 'deliveryStatus', label: 'Delivery status', group: 'Kit & outreach', options: poolUS('delivery_values') },
    // ── Results ──
    { id: 'resultValue', label: 'Result value', group: 'Results', options: RESULT_VALUE_LABELS.filter((l) => rvPresent.has(l)) },
    { id: 'resultStatus', label: 'Result status', group: 'Results', options: RESULT_STATUS_LABELS.filter((l) => rsPresent.has(l)) },
  ];
  return axes.filter((a) => a.options.length > 0);
}

// ── Shipping tab (shipment-level axes) ───────────────────────────────────────

const SHIP_KIND_LABEL: Record<string, string> = { kit: 'Kit', letter: 'Letter' };

function shipOutbound(r: ShippingRow): string {
  return (r.kind === 'kit' ? r.outbound_status : r.delivery_status) || 'No scan yet';
}
function shipReturn(r: ShippingRow): string {
  return r.kind === 'kit' ? r.return_status || 'No scan yet' : '';
}
function shipCarrier(r: ShippingRow): string {
  return r.kind === 'letter' ? r.carrier : '';
}

export function matchShippingOps(r: ShippingRow, f: LookupFilters): boolean {
  if (!axisPass(f, 'shipKind', SHIP_KIND_LABEL[r.kind] ?? r.kind)) return false;
  if (!axisPass(f, 'outboundStatus', shipOutbound(r))) return false;
  // Return status / carrier only meaningfully apply to their kind; rows of the other kind
  // pass only when nothing is selected on that axis (their value is '').
  if (!axisPass(f, 'returnStatus', shipReturn(r))) return false;
  if (!axisPass(f, 'carrier', shipCarrier(r))) return false;
  return true;
}

export function buildShippingAxes(rows: ShippingRow[]): LookupAxis[] {
  if (rows.length === 0) return [];
  const axes: LookupAxis[] = [
    { id: 'shipKind', label: 'Type', group: 'Shipment', options: uniq(rows.map((r) => SHIP_KIND_LABEL[r.kind] ?? r.kind)) },
    { id: 'outboundStatus', label: 'Outbound status', group: 'Shipment', options: uniq(rows.map(shipOutbound)) },
    { id: 'returnStatus', label: 'Return status', group: 'Shipment', options: uniq(rows.map(shipReturn)) },
    { id: 'carrier', label: 'Carrier', group: 'Shipment', options: uniq(rows.map(shipCarrier)) },
  ];
  return axes.filter((a) => a.options.length > 0);
}

// ── Enroute tab (kits), row 418 ──────────────────────────────────────────────
//
// The Portal's EnrouteSlicerRail axes (v1/src/pages/operations/EnrouteSlicerRail.tsx), chip-shaped:
// Geography, Demographics, Program, Screening history, Reachability, Kit & outreach (kit stage and
// the delivery status), plus the lab milestone and the shipped month. The Portal's date range is a
// pair of date inputs; the generic rail here is chips, so the SHIPPED MONTH stands in for it (said in
// the record). Its "Outbound called" and "PCP letter on file" tri-states need members.outbound_call_count
// and pcp_letter_count, which Care's kit row does not carry.

export function shippedMonth(v: string): string {
  const s = String(v ?? '').trim();
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s);
  if (us) return `${us[3]}-${us[1]!.padStart(2, '0')}`;
  const iso = /^(\d{4})-(\d{2})/.exec(s);
  return iso ? `${iso[1]}-${iso[2]}` : 'Unknown';
}

function enrouteDelivery(r: EnrouteKitRow): string {
  return r.outbound_status_category || 'No scan yet';
}
function enrouteReturn(r: EnrouteKitRow): string {
  return r.return_status_category || 'No scan yet';
}
function enrouteResulted(r: EnrouteKitRow): string {
  return r.resulted_ts ? 'Resulted' : 'Not resulted';
}

export function matchEnrouteOps(r: EnrouteKitRow, f: LookupFilters): boolean {
  if (!axisPass(f, 'state', r.state)) return false;
  if (!axisPass(f, 'county', r.county)) return false;
  if (!axisPass(f, 'zip', r.zip)) return false;
  if (!axisPass(f, 'market', r.market)) return false;
  if (!axisPass(f, 'gender', r.gender)) return false;
  if (!axisPass(f, 'ageBand', ageBand(r.age))) return false;
  if (!axisPass(f, 'language', r.language)) return false;
  if (!axisPass(f, 'cohort', r.cohort)) return false;
  if (!axisPass(f, 'mso', r.mso)) return false;
  if (!axisPass(f, 'ipa', r.ipa)) return false;
  if (!axisPass(f, 'comp2025', compLabel(r.comp_2025))) return false;
  if (!axisPass(f, 'comp2024', compLabel(r.comp_2024))) return false;
  if (!axisPass(f, 'hasEmail', r.has_email ? 'Has email' : 'No email')) return false;
  if (!axisPass(f, 'hasPhone', r.has_phone ? 'Has phone' : 'No phone')) return false;
  if (!axisPass(f, 'kitStage', r.kit_stage)) return false;
  if (!axisPass(f, 'deliveryStatus', enrouteDelivery(r))) return false;
  if (!axisPass(f, 'returnStatus', enrouteReturn(r))) return false;
  if (!axisPass(f, 'resulted', enrouteResulted(r))) return false;
  if (!axisPass(f, 'shippedMonth', shippedMonth(r.ship_date))) return false;
  return true;
}

export function buildEnrouteAxes(rows: EnrouteKitRow[]): LookupAxis[] {
  if (rows.length === 0) return [];
  const ageBandsPresent = new Set(rows.map((r) => ageBand(r.age)));
  const axes: LookupAxis[] = [
    { id: 'state', label: 'State', group: 'Geography', options: uniq(rows.map((r) => r.state)) },
    { id: 'county', label: 'County', group: 'Geography', options: uniq(rows.map((r) => r.county)), searchable: true },
    { id: 'zip', label: 'ZIP', group: 'Geography', options: uniq(rows.map((r) => r.zip)), searchable: true },
    { id: 'market', label: 'Market', group: 'Geography', options: uniq(rows.map((r) => r.market)) },
    { id: 'gender', label: 'Gender', group: 'Demographics', options: uniq(rows.map((r) => r.gender)) },
    { id: 'ageBand', label: 'Age band', group: 'Demographics', options: [...AGE_BANDS, 'Unknown'].filter((b) => ageBandsPresent.has(b)) },
    { id: 'language', label: 'Language', group: 'Demographics', options: uniq(rows.map((r) => r.language)), searchable: true },
    { id: 'cohort', label: 'Cohort', group: 'Program', options: uniq(rows.map((r) => r.cohort)), labelFor: cohortLabel },
    { id: 'mso', label: 'MSO', group: 'Program', options: uniq(rows.map((r) => r.mso)), searchable: true },
    { id: 'ipa', label: 'IPA', group: 'Program', options: uniq(rows.map((r) => r.ipa)), searchable: true },
    { id: 'comp2025', label: '2025 status', group: 'Screening history', options: COMP_LABELS.filter((l) => rows.some((r) => compLabel(r.comp_2025) === l)) },
    { id: 'comp2024', label: '2024 status', group: 'Screening history', options: COMP_LABELS.filter((l) => rows.some((r) => compLabel(r.comp_2024) === l)) },
    { id: 'hasEmail', label: 'Has email', group: 'Reachability', options: uniq(rows.map((r) => (r.has_email ? 'Has email' : 'No email'))) },
    { id: 'hasPhone', label: 'Has phone', group: 'Reachability', options: uniq(rows.map((r) => (r.has_phone ? 'Has phone' : 'No phone'))) },
    { id: 'kitStage', label: 'Kit stage', group: 'Kit & outreach', options: uniq(rows.map((r) => r.kit_stage)) },
    { id: 'deliveryStatus', label: 'Delivery status', group: 'Kit & outreach', options: uniq(rows.map(enrouteDelivery)) },
    { id: 'returnStatus', label: 'Return status', group: 'Kit & outreach', options: uniq(rows.map(enrouteReturn)) },
    { id: 'resulted', label: 'Lab milestone', group: 'Kit & outreach', options: ['Resulted', 'Not resulted'].filter((v) => rows.some((r) => enrouteResulted(r) === v)) },
    { id: 'shippedMonth', label: 'Shipped (month)', group: 'Time', options: uniq(rows.map((r) => shippedMonth(r.ship_date))) },
  ];
  return axes.filter((a) => a.options.length > 0);
}

// ── Results tab, row 418 ───────────────────────────────────────────────────────
//
// The Portal's generic rail on the Results rows (buildResultsAxes / matchResultsOps in
// MemberLookup.tsx): the member dimensions plus the result value and flag. These rows ARE an outcome
// disclosure by design (the tab is reason-gated and audited as such), so filtering on the result
// here is the operator narrowing what she has already been shown, not a new disclosure; the
// Member tab's browse row stays option 4 and never reaches this function.

function resultsPositive(r: ResultsRow): string {
  return r.is_positive ? 'Positive' : 'Not positive';
}
function resultsFaxed(r: ResultsRow): string {
  return r.pcp_faxed_ts ? 'PCP faxed' : 'PCP not faxed';
}
function resultsLetter(r: ResultsRow): string {
  return r.member_letter_ts ? 'Letter mailed' : 'No letter yet';
}

export function matchResultsOps(r: ResultsRow, f: LookupFilters): boolean {
  if (!axisPass(f, 'state', r.state)) return false;
  if (!axisPass(f, 'county', r.county)) return false;
  if (!axisPass(f, 'zip', r.zip)) return false;
  if (!axisPass(f, 'gender', r.gender)) return false;
  if (!axisPass(f, 'ageBand', ageBand(r.age))) return false;
  if (!axisPass(f, 'language', r.language)) return false;
  if (!axisPass(f, 'cohort', r.cohort)) return false;
  if (!axisPass(f, 'mso', r.mso)) return false;
  if (!axisPass(f, 'ipa', r.ipa)) return false;
  if (!axisPass(f, 'comp2025', compLabel(r.comp_2025))) return false;
  if (!axisPass(f, 'comp2024', compLabel(r.comp_2024))) return false;
  if (!axisPass(f, 'hasEmail', r.has_email ? 'Has email' : 'No email')) return false;
  if (!axisPass(f, 'hasPhone', r.has_phone ? 'Has phone' : 'No phone')) return false;
  if (!axisPass(f, 'doNotSolicit', dnsCharm(r) ? 'Do not solicit' : '')) return false;
  if (!axisPass(f, 'resultValue', r.result_label)) return false;
  if (!axisPass(f, 'resultStatus', r.status_label)) return false;
  if (!axisPass(f, 'positive', resultsPositive(r))) return false;
  if (!axisPass(f, 'pcpFaxed', resultsFaxed(r))) return false;
  if (!axisPass(f, 'memberLetter', resultsLetter(r))) return false;
  return true;
}

/** Label ordering for a label axis: the fixed table first, then anything the rows carry beyond it. */
function orderedLabels(values: string[], table: readonly string[]): string[] {
  const known = table.filter((l) => values.includes(l));
  const extra = values.filter((l) => !table.includes(l)).sort();
  return [...known, ...extra];
}

export function buildResultsAxes(rows: ResultsRow[]): LookupAxis[] {
  if (rows.length === 0) return [];
  const ageBandsPresent = new Set(rows.map((r) => ageBand(r.age)));
  const axes: LookupAxis[] = [
    { id: 'state', label: 'State', group: 'Geography', options: uniq(rows.map((r) => r.state)) },
    { id: 'county', label: 'County', group: 'Geography', options: uniq(rows.map((r) => r.county)), searchable: true },
    { id: 'zip', label: 'ZIP', group: 'Geography', options: uniq(rows.map((r) => r.zip)), searchable: true },
    { id: 'gender', label: 'Gender', group: 'Demographics', options: uniq(rows.map((r) => r.gender)) },
    { id: 'ageBand', label: 'Age band', group: 'Demographics', options: [...AGE_BANDS, 'Unknown'].filter((b) => ageBandsPresent.has(b)) },
    { id: 'language', label: 'Language', group: 'Demographics', options: uniq(rows.map((r) => r.language)), searchable: true },
    { id: 'cohort', label: 'Cohort', group: 'Program', options: uniq(rows.map((r) => r.cohort)), labelFor: cohortLabel },
    { id: 'mso', label: 'MSO', group: 'Program', options: uniq(rows.map((r) => r.mso)), searchable: true },
    { id: 'ipa', label: 'IPA', group: 'Program', options: uniq(rows.map((r) => r.ipa)), searchable: true },
    { id: 'comp2025', label: '2025 status', group: 'Screening history', options: COMP_LABELS.filter((l) => rows.some((r) => compLabel(r.comp_2025) === l)) },
    { id: 'comp2024', label: '2024 status', group: 'Screening history', options: COMP_LABELS.filter((l) => rows.some((r) => compLabel(r.comp_2024) === l)) },
    { id: 'hasEmail', label: 'Has email', group: 'Reachability', options: uniq(rows.map((r) => (r.has_email ? 'Has email' : 'No email'))) },
    { id: 'hasPhone', label: 'Has phone', group: 'Reachability', options: uniq(rows.map((r) => (r.has_phone ? 'Has phone' : 'No phone'))) },
    { id: 'doNotSolicit', label: 'Do not solicit', group: 'Status', options: rows.some((r) => dnsCharm(r)) ? ['Do not solicit'] : [] },
    { id: 'resultValue', label: 'Result', group: 'Results', options: orderedLabels(uniq(rows.map((r) => r.result_label)), RESULT_VALUE_LABELS) },
    { id: 'resultStatus', label: 'Flag', group: 'Results', options: orderedLabels(uniq(rows.map((r) => r.status_label)), RESULT_STATUS_LABELS) },
    { id: 'positive', label: 'Positive', group: 'Results', options: ['Positive', 'Not positive'].filter((v) => rows.some((r) => resultsPositive(r) === v)) },
    { id: 'pcpFaxed', label: 'PCP faxed', group: 'Handling', options: ['PCP faxed', 'PCP not faxed'].filter((v) => rows.some((r) => resultsFaxed(r) === v)) },
    { id: 'memberLetter', label: 'Member letter', group: 'Handling', options: ['Letter mailed', 'No letter yet'].filter((v) => rows.some((r) => resultsLetter(r) === v)) },
  ];
  return axes.filter((a) => a.options.length > 0);
}
